import { Chip } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { marketsForScope } from '../dataProvider/types';
import type { Testimonial } from '../dataProvider';

/**
 * Marketing-site testimonials list (D-cms-testimonials-seo). A FLAT structured surface,
 * NO rich text: quote, author, rating, market and the active / inactive toggle.
 * OPERATIONAL market-scoped: the switcher governs which testimonials show (the active
 * markets PLUS the all-market ones, the RuleMarket idiom). ROLE-gated to editor and
 * super_admin (a capability read-gate would leak via a viewer's '*:read'). Authoring
 * gates cms:author. There is NO publish lifecycle, only the on / off toggle.
 */

const testimonialFilters = [
  <TextInput key="q" source="q" label="Search quote" alwaysOn resettable />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'active', name: 'Active' },
      { id: 'inactive', name: 'Inactive' },
    ]}
  />,
];

const TestimonialListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canAuthor = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:author' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canAuthor && <CreateButton label="New testimonial" />}
    </TopToolbar>
  );
};

const truncate = (s: string, n = 80) =>
  s.length > n ? `${s.slice(0, n)}...` : s;

/** The list itself. Exported for testing without the access wrapper. */
export const TestimonialListView = () => {
  const { activeRegion } = useRegion();
  // The active markets PLUS the all-market rows (a both row shows in every scope).
  const markets = [...marketsForScope(activeRegion ?? 'GLOBAL'), 'all'];
  return (
    <List
      resource="testimonials"
      title="Testimonials"
      filter={{ market: markets }}
      filters={testimonialFilters}
      actions={<TestimonialListActions />}
      sort={{ field: 'author', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <FunctionField
          label="Quote"
          render={(r: Testimonial) => truncate(r.quote)}
        />
        <TextField source="author" label="Author" />
        <FunctionField
          label="Rating"
          render={(r: Testimonial) => '*'.repeat(r.rating)}
        />
        <FunctionField
          label="Market"
          render={(r: Testimonial) => String(r.market).toUpperCase()}
        />
        <FunctionField
          label="Status"
          render={(r: Testimonial) => (
            <Chip
              size="small"
              variant={r.status === 'active' ? 'filled' : 'outlined'}
              color={r.status === 'active' ? 'success' : 'default'}
              label={r.status === 'active' ? 'Active' : 'Inactive'}
              data-testid={`testimonial-status-${r.id}`}
            />
          )}
        />
      </Datagrid>
    </List>
  );
};

/** Gated entry point. ROLE-gated to editor and super_admin. */
export const TestimonialList = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <TestimonialListView />
  </RequireAccess>
);
